import type { DateTime } from "luxon";
import { DailyPrintoutGroupingOption } from "../../../utils/models";
import { buildCanonicalFanOutMap } from "../../../utils/timeGrouping";
import type { ScheduleRow } from "../types";
import { groupKeyFor } from "./groupKey";

/**
 * The result of §6.3 popularity-anchored shift-time grouping.
 *
 * - `groups` — final buckets keyed by canonical window label, in **descending row-count order**
 *   (popularity). The order in which entries are inserted matches the canonicalization pass.
 * - `canonicalStart` / `canonicalEnd` — for each canonical group, the anchor bucket's actual
 *   start / end timestamps. These are the values §5.7 group sort uses for the `DateTime` compare
 *   step, replacing today's "first row in group" approximation.
 */
export interface ShiftTimeCanonicalResult {
	groups: Map<string, ScheduleRow[]>;
	canonicalStart: Map<string, DateTime>;
	canonicalEnd: Map<string, DateTime>;
}

interface ExactBucket {
	key: string;
	rows: ScheduleRow[];
	start: DateTime;
	end: DateTime;
}

/**
 * Group rows by Shift Time with §6.3 popularity-anchored proximity merging.
 *
 * 1. Bucket rows by the exact `"HH:MM-HH:MM"` window key
 * 2. Sort buckets by row count descending — the most-populated bucket wins
 * 3. Build a canonical fan-out map via the shared `buildCanonicalFanOutMap` (uses
 *    `MAX_GROUP_MERGE_DIFF_MIN` proximity logic on minutes-since-epoch). Buckets within the proximity
 *    window collapse into whichever popular anchor they're closest to; an unpopular wide bucket that
 *    fully covers 2+ popular anchors fans out across each of those anchors instead.
 * 4. Re-bucket rows by their canonical key(s) — a row in a covering bucket lands in every popular
 *    anchor it spans. The order of `groups.keys()` is the order anchors were encountered.
 *
 * Returns the canonical start/end timestamps so §5.7 group sort can use them as DateTime values
 * for the shift-time level (mapping §5.7 implementation contract).
 */
export const groupRowsByCanonicalShiftTime = (rows: ScheduleRow[]): ShiftTimeCanonicalResult => {
	const buckets = new Map<string, ExactBucket>();
	for (const row of rows) {
		const key = groupKeyFor({ row, groupingOption: DailyPrintoutGroupingOption.START_TIME, jobManagementEnabled: false });
		const existing = buckets.get(key);
		if (existing) {
			existing.rows.push(row);
		} else {
			buckets.set(key, { key, rows: [row], start: row.shiftStart, end: row.shiftEnd });
		}
	}

	// Sort by row count desc, then by duration desc as tiebreaker. The duration tiebreaker is
	// load-bearing: when two buckets have the same row count and one is fully contained in the
	// other (e.g. 2:30p-6:30p ⊂ 2:30p-10:30p), the longer one needs to win the canonical anchor
	// position. Otherwise `isContained(longer inside shorter)` fails (the longer can't fit), and
	// the two end up as separate canonical groups even though they should merge.
	const windowDurationMin = (b: ExactBucket): number => Math.floor((b.end.toMillis() - b.start.toMillis()) / 60_000);
	const sortedBuckets = [...buckets.values()].sort((a, b) => b.rows.length - a.rows.length || windowDurationMin(b) - windowDurationMin(a));
	const asMinutes = sortedBuckets.map(({ key, start, end }) => ({
		key,
		startMin: Math.floor(start.toMillis() / 60_000),
		endMin: Math.floor(end.toMillis() / 60_000),
	}));
	const bucketToTargets = buildCanonicalFanOutMap(asMinutes);

	const groups = new Map<string, ScheduleRow[]>();
	const canonicalStart = new Map<string, DateTime>();
	const canonicalEnd = new Map<string, DateTime>();

	// First pass: claim canonical-anchor start/end times in popularity order so §5.7 group sort
	// uses the most-populated bucket's timestamps for the canonical group. A bucket is a real anchor
	// when its target list is exactly itself; spreaders (which fan out into the popular anchors they
	// cover) are skipped here so they never become a group of their own.
	for (const bucket of sortedBuckets) {
		const targets = bucketToTargets.get(bucket.key) ?? [bucket.key];
		const isAnchor = targets.length === 1 && targets[0] === bucket.key;
		if (isAnchor && !canonicalStart.has(bucket.key)) {
			canonicalStart.set(bucket.key, bucket.start);
			canonicalEnd.set(bucket.key, bucket.end);
		}
	}

	// Second pass: walk rows in their ORIGINAL Phase 2 order, routing each to its canonical group(s).
	// This preserves the user's sortBy ordering within each canonical section. Iterating buckets
	// instead (popularity order) would cluster rows by bucket and break the name sort. A row in a
	// spreader bucket fans out into each popular anchor that bucket covers (§6.3 wide-window spread).
	for (const row of rows) {
		const exactKey = groupKeyFor({ row, groupingOption: DailyPrintoutGroupingOption.START_TIME, jobManagementEnabled: false });
		const targets = bucketToTargets.get(exactKey) ?? [exactKey];
		for (const canonicalKey of targets) {
			const existing = groups.get(canonicalKey);
			if (existing) existing.push(row);
			else groups.set(canonicalKey, [row]);
		}
	}

	return { groups, canonicalStart, canonicalEnd };
};
